/**
 * Utility functions for calculating monthly salary costs based on payment frequency
 */

/**
 * Count how many times a specific day of week occurs in a given month
 * @param year - The year (e.g., 2026)
 * @param month - The month (0-11, January = 0)
 * @param paymentDayOfWeek - Day of week (0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday)
 * @returns Number of occurrences of that day in the month
 */
export function getPaymentDaysInMonth(
  year: number,
  month: number,
  paymentDayOfWeek: number = 5 // Default to Friday
): number {
  const lastDay = new Date(year, month + 1, 0);
  let count = 0;
  
  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d);
    if (date.getDay() === paymentDayOfWeek) {
      count++;
    }
  }
  
  return count;
}

/**
 * Get the name of a day of week in Portuguese
 */
export function getDayOfWeekName(dayOfWeek: number): string {
  const names: Record<number, string> = {
    0: 'domingos',
    1: 'segundas',
    2: 'terças',
    3: 'quartas',
    4: 'quintas',
    5: 'sextas',
    6: 'sábados',
  };
  return names[dayOfWeek] || 'dias';
}

/**
 * Get month name in Portuguese
 */
export function getMonthName(month: number): string {
  const names = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  return names[month] || '';
}

interface EmployeeSalaryData {
  base_salary?: number | null;
  salary_frequency?: string | null;
  weekly_payment_type?: string | null;
  hourly_rate?: number | null;
  projected_hours_per_week?: number | null;
  payment_day_of_week?: number | null;
  salary_currency?: string | null;
  benefits_cost?: number | null;
}

/**
 * Calculate the monthly salary cost for an employee based on their payment frequency
 * @param employee - Employee salary data
 * @param year - Year for calculation
 * @param month - Month for calculation (0-11)
 * @returns Monthly cost in the employee's currency
 */
export function calculateMonthlySalaryCost(
  employee: EmployeeSalaryData,
  year: number,
  month: number
): number {
  const frequency = employee.salary_frequency || 'monthly';
  const baseSalary = Number(employee.base_salary) || 0;
  
  if (frequency === 'monthly') {
    // Monthly salary - return as is
    return baseSalary;
  }
  
  if (frequency === 'biweekly') {
    // Biweekly (quinzenal) = 2x per month
    return baseSalary * 2;
  }
  
  if (frequency === 'weekly') {
    // Weekly payment
    const paymentDayOfWeek = employee.payment_day_of_week ?? 5; // Default to Friday
    const weeksInMonth = getPaymentDaysInMonth(year, month, paymentDayOfWeek);
    
    if (employee.weekly_payment_type === 'hourly') {
      // Hourly: value/hour × hours/week × weeks in month
      const hourlyRate = Number(employee.hourly_rate) || 0;
      const hoursPerWeek = Number(employee.projected_hours_per_week) || 0;
      const weeklyValue = hourlyRate * hoursPerWeek;
      return weeklyValue * weeksInMonth;
    } else {
      // Fixed weekly value × weeks in month
      return baseSalary * weeksInMonth;
    }
  }
  
  // Fallback
  return baseSalary;
}

/**
 * Calculate the monthly salary cost with benefits
 * @param employee - Employee salary data
 * @param year - Year for calculation
 * @param month - Month for calculation (0-11)
 * @param exchangeRate - Exchange rate (BRL to USD divisor, e.g., 5.5)
 * @returns Monthly cost in USD
 */
export function calculateMonthlySalaryCostUSD(
  employee: EmployeeSalaryData,
  year: number,
  month: number,
  exchangeRate: number = 5.5
): number {
  const salaryCost = calculateMonthlySalaryCost(employee, year, month);
  const benefitsCost = Number(employee.benefits_cost) || 0;
  const rawCost = salaryCost + benefitsCost;
  
  // Convert to USD if currency is BRL
  return employee.salary_currency === 'BRL' ? rawCost / exchangeRate : rawCost;
}
